import { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axios.client';

export const useUserSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user settings from backend
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      // Check if user is authenticated
      const token = localStorage.getItem('tkn');
      const user = localStorage.getItem('user');

      if (!token || !user) {
        console.log('No authentication token or user data found, using default settings');
        console.log('Token present:', !!token);
        console.log('User data present:', !!user);
        setSettings({
          chatSettings: {
            selectedModel: 'llama4',
            temperature: 0.7,
            playAudio: false,
          },
          uiSettings: {
            theme: 'auto',
            fontSize: 'medium',
          },
        });
        return;
      }

      const response = await axiosClient.get('users/settings');
      console.log('✅ User settings loaded:', response);
      setSettings(response.settings || {});
    } catch (error) {
      console.error('Failed to load user settings:', error);

      // Check if it's an authentication error
      if (error.status === 401 || error.status === 403 || error.message?.includes('Unauthorized')) {
        console.log('Authentication failed, using default settings');
        // Clear invalid token
        localStorage.removeItem('tkn');
      } else if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        console.log('Network error - backend may not be running');
      } else {
        console.log('Unknown error type:', error);
      }

      // Set default settings if loading fails
      setSettings({
        chatSettings: {
          selectedModel: 'llama4',
          temperature: 0.7,
          playAudio: false,
        },
        uiSettings: {
          theme: 'auto',
          fontSize: 'medium',
        },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Update chat settings
  const updateChatSettings = useCallback(async (newChatSettings) => {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('tkn');
      if (!token) {
        console.log('No authentication token found, updating local settings only');
        const updatedSettings = {
          ...settings,
          chatSettings: {
            ...settings?.chatSettings,
            ...newChatSettings,
          },
        };
        setSettings(updatedSettings);
        return;
      }

      const updatedSettings = {
        ...settings,
        chatSettings: {
          ...settings?.chatSettings,
          ...newChatSettings,
        },
      };

      await axiosClient.put('users/settings', {
        chatSettings: updatedSettings.chatSettings,
      });

      setSettings(updatedSettings);
    } catch (error) {
      console.error('Failed to update chat settings:', error);

      // If authentication failed, still update local settings
      if (error.status === 401 || error.status === 403) {
        console.log('Authentication failed, updating local settings only');
        const updatedSettings = {
          ...settings,
          chatSettings: {
            ...settings?.chatSettings,
            ...newChatSettings,
          },
        };
        setSettings(updatedSettings);
      }
    }
  }, [settings]);

  // Update UI settings
  const updateUISettings = useCallback(async (newUISettings) => {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('tkn');
      if (!token) {
        console.log('No authentication token found, updating local settings only');
        const updatedSettings = {
          ...settings,
          uiSettings: {
            ...settings?.uiSettings,
            ...newUISettings,
          },
        };
        setSettings(updatedSettings);
        return;
      }

      const updatedSettings = {
        ...settings,
        uiSettings: {
          ...settings?.uiSettings,
          ...newUISettings,
        },
      };

      await axiosClient.put('users/settings', {
        uiSettings: updatedSettings.uiSettings,
      });

      setSettings(updatedSettings);
    } catch (error) {
      console.error('Failed to update UI settings:', error);

      // If authentication failed, still update local settings
      if (error.status === 401 || error.status === 403) {
        console.log('Authentication failed, updating local settings only');
        const updatedSettings = {
          ...settings,
          uiSettings: {
            ...settings?.uiSettings,
            ...newUISettings,
          },
        };
        setSettings(updatedSettings);
      }
    }
  }, [settings]);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Check if user is properly authenticated
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('tkn');
    const user = localStorage.getItem('user');
    return !!(token && user);
  }, []);

  // Check if backend is available
  const checkBackendAvailability = useCallback(async () => {
    try {
      // Try the health endpoint at the root level
      const response = await fetch('/health');
      if (response.ok) {
        return true;
      }
      return false;
    } catch (error) {
      console.log('Backend not available:', error);
      return false;
    }
  }, []);

  return {
    settings,
    loading,
    updateChatSettings,
    updateUISettings,
    loadSettings,
    checkBackendAvailability,
    isAuthenticated,
  };
};
